import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useForm, z, zodResolver } from "@/hooks/schema";
import { Form } from "@/components/Form";
import { Check, CirclePlus } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { registerHolidaysData } from "@/Api/holidays";
import { useToastNavigate } from "@/utils/toastNavigate";
import { LoaderSpin } from "@/components/Loaders";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { HOLIDAY_TYPE_CONST } from "@/constants/HolidayTypeConst";

const HOLIDAY_TYPES = [
  { value: HOLIDAY_TYPE_CONST.NATIONAL, label: "Nacional" },
  { value: HOLIDAY_TYPE_CONST.STATE, label: "Estadual" },
  { value: HOLIDAY_TYPE_CONST.MUNICIPAL, label: "Municipal" },
  { value: HOLIDAY_TYPE_CONST.OPTIONAL, label: "Facultativo" },
];

const schema = z.object({
  name: z.string({ message: "*requerido" }).min(1, "*requerido"),
  date: z.string({ message: "*requerido" }).min(1, "*requerido"),
  holiday_type_id: z.string({ message: "*requerido" }).min(1, "*requerido"),
  is_recurring_yearly: z.boolean().optional(),
  state: z.string().optional(),
  city: z.string().optional(),
});

type SchemaType = z.infer<typeof schema>;

export const AddBtn = ({ updateFront }) => {
  const [isOpen, setIsOpen] = useState(false);

  const form = useForm<SchemaType>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      date: "",
      holiday_type_id: String(HOLIDAY_TYPE_CONST.NATIONAL),
      is_recurring_yearly: false,
      state: "",
      city: "",
    },
  });

  const { toast } = useToastNavigate();
  const [formState, setFormState] = useState<string>("default");
  const formWatch = form.watch();

  const formSubmit = async (data: SchemaType) => {
    setFormState("loading");
    const payload = {
      ...data,
      state: data.state || null,
      city: data.city || null,
    };

    const { data: apiResponse, ok } = await registerHolidaysData(payload);
    if (!ok) {
      setFormState("error");

      setTimeout(() => {
        setFormState("default");
      }, 1000);
      setIsOpen(false);
      return;
    }

    updateFront((prev) => {
      if (prev?.data.length < 15)
        return {
          ...prev,
          data: [apiResponse?.response, ...prev.data],
        };

      prev?.data.pop();
      return {
        ...prev,
        data: [apiResponse?.response, ...prev.data],
        last_page: prev.last_page + 1,
      };
    });

    form.reset();
    setFormState("default");
    setIsOpen(false);
    toast({
      description: apiResponse?.message,
      action: <Check color="#1edc2b" />,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger>
        <Button
          variant="outline"
          className={cn("py-3 flex gap-1", `bg-primary`)}
        >
          <CirclePlus className="size-5" strokeWidth={1.75} />
          Adicionar Feriado
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader className="flex flex-col gap-2">
          <DialogDescription className="text-lg">
            Adicionar Feriado
          </DialogDescription>
          <DialogTitle className="text-2xl">Informações Básicas</DialogTitle>
        </DialogHeader>
        <Form
          onSubmit={form.handleSubmit(formSubmit)}
          {...form}
          className="space-y-3"
        >
          <Form.Row>
            <Form.Field name="name">
              {({ field }) => (
                <Form.Item>
                  <Form.Label>
                    Nome <Form.Message />
                  </Form.Label>
                  <Form.Control>
                    <Input {...field} placeholder="Insira um nome" />
                  </Form.Control>
                </Form.Item>
              )}
            </Form.Field>
          </Form.Row>

          <Form.Row>
            <Form.Field name="date">
              {({ field }) => (
                <Form.Item>
                  <Form.Label>
                    Data <Form.Message />
                  </Form.Label>
                  <Form.Control>
                    <Input {...field} type="date" />
                  </Form.Control>
                </Form.Item>
              )}
            </Form.Field>
            <Form.Field name="holiday_type_id">
              {({ field }) => (
                <Form.Item>
                  <Form.Label>
                    Tipo <Form.Message />
                  </Form.Label>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione um tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      {HOLIDAY_TYPES.map((type) => (
                        <SelectItem
                          key={type.value}
                          value={type.value.toString()}
                        >
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Form.Item>
              )}
            </Form.Field>
          </Form.Row>

          <Form.Row>
            <Form.Field name="state">
              {({ field }) => (
                <Form.Item>
                  <Form.Label>UF</Form.Label>
                  <Form.Control>
                    <Input {...field} placeholder="Ex: SP" maxLength={2} />
                  </Form.Control>
                </Form.Item>
              )}
            </Form.Field>
            <Form.Field name="city">
              {({ field }) => (
                <Form.Item>
                  <Form.Label>Cidade</Form.Label>
                  <Form.Control>
                    <Input {...field} placeholder="Insira uma cidade" />
                  </Form.Control>
                </Form.Item>
              )}
            </Form.Field>
          </Form.Row>

          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={formWatch?.is_recurring_yearly}
              onCheckedChange={(checked) =>
                form.setValue("is_recurring_yearly", !!checked)
              }
            />
            Recorrente todo ano
          </label>

          <DialogFooter>
            <Button
              type="submit"
              className={cn("w-full", colors.bg_primary)}
              disabled={formState !== "default"}
            >
              {formState === "loading" && (
                <>
                  Adicionando <LoaderSpin />
                </>
              )}
              {formState === "default" && "Adicionar"}
              {formState === "error" && "Erro ao adicionar"}
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
